import React, { useState, useEffect, useCallback } from 'react';
import {
  Phone, Mail, MapPin, Download, RefreshCw, FileText, AlertCircle, X, CreditCard,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { buildApiUrl } from '../../utils/apiSetup';
import EmployeeAvatar from '../common/EmployeeAvatar';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token')}`,
});

const DisplayField = ({ label, value }) => (
  <div>
    <p className="text-xs font-medium text-gray-500">{label}</p>
    <p className="text-sm font-semibold text-gray-900 mt-0.5">{value || '—'}</p>
  </div>
);

/**
 * Self-service profile: Read-only profile data & documents viewer for employees.
 */
const EmployeeSelfServiceProfile = ({ photoRefreshKey = 0 }) => {
  const { user } = useAuth();
  const employeeId = user?.employee_id;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [personal, setPersonal] = useState(null);
  const [employment, setEmployment] = useState(null);
  const [payroll, setPayroll] = useState(null);
  const [documents, setDocuments] = useState([]);

  const load = useCallback(async () => {
    if (!employeeId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [profileRes, docsRes] = await Promise.all([
        fetch(buildApiUrl(`/api/v1/employee-master/${employeeId}/profile-360`), { headers: authHeaders() }),
        fetch(buildApiUrl(`/api/v1/employee-master/${employeeId}/documents`), { headers: authHeaders() }),
      ]);

      if (profileRes.ok) {
        const data = await profileRes.json();
        setPersonal(data.personal || {});
        setEmployment(data.employment || {});
        setPayroll(data.payroll || {});
      } else {
        setError('Could not load your profile');
      }

      if (docsRes.ok) {
        setDocuments(await docsRes.json());
      } else {
        setDocuments([]);
      }
    } catch {
      setError('Network error loading profile');
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadDoc = async (docId, fileName) => {
    try {
      const res = await fetch(buildApiUrl(`/api/v1/employee-master/${employeeId}/documents/${docId}/download`), {
        headers: authHeaders(),
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'document';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Document download failed:', e);
    }
  };

  if (!employeeId) {
    return (
      <div className="p-8 text-center text-gray-500">
        Your account is not linked to an employee record. Contact HR.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const displayName = personal?.full_name || user?.employee_name || '—';

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-4">
      {error && (
        <div className="p-4 rounded-lg flex items-start gap-2 bg-red-50 text-red-800">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <p className="text-sm">{error}</p>
          <button type="button" className="ml-auto" onClick={() => setError('')}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Identity Header */}
      <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
        <div className="flex flex-wrap items-start gap-6">
          <div className="relative">
            <EmployeeAvatar
              employeeId={employeeId}
              refreshKey={photoRefreshKey}
              size="xl"
              borderClass="border-2 border-gray-200"
            />
          </div>
          <div className="flex-1 min-w-[200px]">
            <h2 className="text-xl font-bold text-gray-900">{displayName}</h2>
            <p className="text-gray-600">{personal?.emp_code || '—'} · {employment?.designation || '—'}</p>
            <p className="text-sm text-gray-500 mt-1">{employment?.department || '—'}</p>
          </div>
          <div className="flex gap-2 ml-auto">
            <button type="button" onClick={load} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1.5 text-sm text-gray-700 font-medium" title="Refresh Profile">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Profile Details (Read Only) */}
      <div className="bg-white rounded-xl shadow border border-gray-100 p-6 space-y-6">
        {/* Contact Info */}
        <h3 className="text-lg font-semibold flex items-center gap-2 border-b pb-2 text-gray-900">
          <Phone className="w-5 h-5 text-blue-600" /> Contact Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DisplayField label="Mobile Number" value={personal?.phone} />
          <DisplayField label="Alternate Mobile" value={personal?.alternate_phone} />
          <DisplayField label="Official Email" value={personal?.email || personal?.official_email} />
          <DisplayField label="Personal Email" value={personal?.personal_email} />
        </div>

        {/* Address Info */}
        <h3 className="text-lg font-semibold flex items-center gap-2 border-b pb-2 text-gray-900 pt-2">
          <MapPin className="w-5 h-5 text-blue-600" /> Address Details
        </h3>
        <div className="grid grid-cols-1 gap-4">
          <DisplayField label="Current Address" value={personal?.current_address} />
          <DisplayField label="Permanent Address" value={personal?.permanent_address} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <DisplayField label="City" value={personal?.city} />
            <DisplayField label="State" value={personal?.state} />
            <DisplayField label="PIN Code" value={personal?.pin_code} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DisplayField label="Marital Status" value={personal?.marital_status ? personal.marital_status.charAt(0).toUpperCase() + personal.marital_status.slice(1) : '—'} />
            <DisplayField label="Blood Group" value={personal?.blood_group} />
          </div>
        </div>

        {/* Bank & Emergency Details */}
        <h3 className="text-lg font-semibold flex items-center gap-2 border-b pb-2 text-gray-900 pt-2">
          <CreditCard className="w-5 h-5 text-blue-600" /> Bank & Emergency Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DisplayField label="Bank Name" value={payroll?.bank_name} />
          <DisplayField label="Account Number" value={payroll?.bank_account_number} />
          <DisplayField label="Account Holder Name" value={payroll?.bank_account_name} />
          <DisplayField label="IFSC Code" value={payroll?.bank_ifsc_code} />
          <DisplayField label="Branch" value={payroll?.bank_branch} />
          <DisplayField label="Emergency Contact Name" value={personal?.emergency_contact_name} />
          <DisplayField label="Emergency Contact Number" value={personal?.emergency_contact_number} />
        </div>

        <p className="text-xs text-gray-500 flex items-center gap-1.5 pt-2 border-t">
          <Mail className="w-4 h-4 text-gray-400 shrink-0" />
          Employee profile information is managed by HR Admin. If any information needs updating, please contact your HR administrator.
        </p>
      </div>

      {/* Documents View Only */}
      <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-4 text-gray-900">
          <FileText className="w-5 h-5 text-blue-600" /> My Documents ({documents.length})
        </h3>

        {documents.length === 0 ? (
          <p className="text-sm text-gray-500 py-6 text-center border rounded-lg bg-gray-50">No documents available on record.</p>
        ) : (
          <ul className="space-y-3">
            {documents.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 border rounded-lg p-4 bg-gray-50 hover:bg-white transition-colors">
                <div>
                  <p className="font-medium text-gray-900">{d.type_name || 'Document'}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{d.file_name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Status: <span className="font-medium text-gray-700 capitalize">{d.verification_status || 'verified'}</span>
                    {d.expiry_date && ` · Expires: ${d.expiry_date}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => downloadDoc(d.id, d.file_name)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg text-xs font-semibold transition-colors"
                    title="Download Document"
                  >
                    <Download className="w-3.5 h-3.5" /> Download
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default EmployeeSelfServiceProfile;
